import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { EMPTY, Observable, from, merge, of, throwError } from 'rxjs';
import { catchError, filter, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { API_BASE_URL } from '../constants/api';
import { ApiService } from '../services/api.service';
import { OfflineCacheService } from './offline-cache.service';
import { PlatformService } from './platform.service';
import { SYNC_REPLAY, SyncEntity, SyncService } from './sync.service';

const QUEUEABLE_WRITES: { pattern: RegExp; entity: SyncEntity }[] = [
  { pattern: /\/vehicle-activity\/sync$/i, entity: 'vehicle_activity' },
  { pattern: /\/facility-activity\/sync$/i, entity: 'facility_activity' },
  { pattern: /\/pass\/create$/i, entity: 'pass' }
];

/** Request headers that carry tenant context and must be replayed; auth headers are re-applied fresh at sync time. */
const CONTEXT_HEADERS = ['x-parking-id', 'x-org-id', 'x-user-pov', 'x-role'];

/**
 * Native-only offline layer. SQLite is the first source, the API updates it afterwards.
 * - Entry/exit/facility writes are stored in the SQLite outbox and answered locally.
 *   SyncService sends them to the backend when the device is online.
 * - Reads paint from SQLite immediately, then refresh from the API and write the new copy back.
 * Must run before the auth/optimization interceptors so it also catches their network failures.
 */
export function offlineInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const platform = inject(PlatformService);
  if (!platform.isNative || request.context.get(SYNC_REPLAY) || request.headers.get('X-Sync-Replay') === '1' || !request.url.startsWith(API_BASE_URL)) {
    if (request.headers.get('X-Sync-Replay') === '1') {
      return next(request.clone({ headers: request.headers.delete('X-Sync-Replay') }));
    }
    return next(request);
  }

  const sync = inject(SyncService);
  const cache = inject(OfflineCacheService);
  const injector = inject(Injector);

  const write = request.method === 'POST' && QUEUEABLE_WRITES.find(({ pattern }) => pattern.test(request.url));
  if (write) {
    return handleWrite(request, next, sync, write.entity, injector.get(ApiService));
  }

  if (request.method === 'GET' && !request.url.includes('/auth/')) {
    return handleRead(request, next, sync, cache, cacheKey(request, injector.get(ApiService)));
  }

  return next(request);
}

function handleWrite(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  sync: SyncService,
  entity: SyncEntity,
  api: ApiService
): Observable<HttpEvent<unknown>> {
  const online = sync.isOnline || (typeof navigator !== 'undefined' && navigator.onLine);
  // Online, and nothing is waiting ahead of this write: send it to the server now.
  if (online && !sync.hasPending()) {
    return next(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
          const body = event.body as { offline?: boolean } | null;
          if (!body?.offline) api.invalidateRequestCache(cacheFragment(entity));
        }
      }),
      catchError((error: unknown) => {
        if (!isNetworkFailure(error)) return throwError(() => error);
        return from(enqueue(request, sync, entity));
      })
    );
  }
  return from(enqueue(request, sync, entity));
}

function cacheFragment(entity: SyncEntity): string {
  if (entity === 'vehicle_activity') return 'vehicle-activity';
  if (entity === 'facility_activity') return 'facility-activity';
  return '/pass';
}

async function enqueue(request: HttpRequest<unknown>, sync: SyncService, entity: SyncEntity): Promise<HttpResponse<unknown>> {
  const body = (request.body ?? {}) as Record<string, any>;
  const offlineReferenceId = String(body['offline_reference_id'] || '');
  if (!offlineReferenceId) {
    throw new Error('Cannot save offline: transaction has no offline_reference_id');
  }

  const op = getCurrentOperator();
  if (!body['inBy'] || body['inBy']?.name === 'Offline Operator') {
    body['inBy'] = {
      id: op.id,
      name: op.name,
      firstName: op.firstName,
      lastName: op.lastName,
      email: op.email
    };
  }
  if (!body['operator'] || body['operator'] === 'Offline Operator') {
    body['operator'] = op.name;
  }

  const headers: Record<string, string> = {};
  for (const name of CONTEXT_HEADERS) {
    const value = request.headers.get(name);
    if (value) headers[name] = value;
  }

  await sync.enqueue({ entity, offlineReferenceId, method: request.method, url: request.url, headers, body });
  if (sync.isOnline || (typeof navigator !== 'undefined' && navigator.onLine)) {
    await sync.flush();
  }

  return new HttpResponse({
    status: 202,
    url: request.url,
    body: {
      success: true,
      offline: true,
      message: 'Saved on this device. It will sync automatically when the internet is back.',
      data: { ...body, id: offlineReferenceId, offline_reference_id: offlineReferenceId, sync_status: 'pending' }
    }
  });
}

function getCurrentOperator(): { id?: string; name: string; firstName?: string; lastName?: string; email?: string } {
  try {
    const raw = localStorage.getItem('user_info') || sessionStorage.getItem('user_info');
    if (raw) {
      const u = JSON.parse(raw);
      const firstName = u.firstName || u.first_name || '';
      const lastName = u.lastName || u.last_name || '';
      let fullName = (firstName && lastName) ? `${firstName} ${lastName}`.trim() : (u.name || u.fullName || u.full_name || '');
      if (!fullName && (firstName || lastName)) {
        fullName = `${firstName} ${lastName}`.trim();
      }
      const name = (fullName || u.username || u.email || '').trim();
      if (name) {
        return {
          id: u.id || u.user_id,
          name,
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          email: u.email
        };
      }
    }
  } catch {}
  return { name: 'Operator' };
}

const KNOWN_VEHICLE_TYPE_IDS: Record<string, { name: string; slug: string }> = {
  '3d3f97d3-4cf0-45a6-9bbd-15ab5a6df8d6': { name: 'Car', slug: 'car' },
  'e7e2ffc1-faec-48b5-b8b1-735f28c1d3ab': { name: 'Bike', slug: 'bike' },
  '6a2aa2ad-cba2-49dc-b82f-531e8b158bf9': { name: 'Cycle', slug: 'cycle' },
};

function matchVehicleFamily(text: string): { name: string; slug: string } | null {
  if (!text) return null;
  const t = text.toLowerCase();
  if (/\b(car|4-wheeler|4w|four|sedan|suv|hatchback|jeep|lmu|cab|taxi)\b/i.test(t) || t.includes('4 wheeler') || t.includes('four wheeler')) {
    return { name: 'Car', slug: 'car' };
  }
  if (/\b(bike|2-wheeler|2w|two|motorcycle|scooter|activa|moped)\b/i.test(t) || t.includes('2 wheeler') || t.includes('two wheeler')) {
    return { name: 'Bike', slug: 'bike' };
  }
  if (/\b(truck|hcv|lorry|heavy|commercial)\b/i.test(t)) {
    return { name: 'Truck', slug: 'truck' };
  }
  if (/\b(bus|coach)\b/i.test(t)) {
    return { name: 'Bus', slug: 'bus' };
  }
  if (/\b(cycle|bicycle)\b/i.test(t)) {
    return { name: 'Cycle', slug: 'cycle' };
  }
  if (/\b(auto|rickshaw|3-wheeler|3w|three)\b/i.test(t) || t.includes('3 wheeler') || t.includes('three wheeler')) {
    return { name: 'Auto Rickshaw', slug: 'autorickshaw' };
  }
  if (/\b(ev|electric)\b/i.test(t)) {
    return { name: 'EV', slug: 'ev' };
  }
  return null;
}

function resolveVehicleType(item: any, cachedServices?: any[]): { name: string; slug: string } {
  if (!item) return { name: 'Car', slug: 'car' };

  const raw = item.rawActivity || {};
  const v = item.vehicle || raw.vehicle || {};
  const vInfo = item.vehicleInfo || raw.vehicleInfo || {};
  const svc = item.service || raw.service || {};

  const directName = (
    item.category_name ||
    raw.category_name ||
    item.vehicle_type_name ||
    raw.vehicle_type_name ||
    item.service_name ||
    raw.service_name ||
    vInfo.vehicleTypeName ||
    v.vehicleType?.name ||
    item.vehicleType?.name ||
    v.name ||
    svc.name ||
    item.category ||
    item.type ||
    ''
  ).toString().trim();

  const directSlug = (
    item.vehicle_type_slug ||
    raw.vehicle_type_slug ||
    item.category_slug ||
    raw.category_slug ||
    vInfo.vehicleTypeSlug ||
    v.vehicleType?.slug ||
    item.vehicleType?.slug ||
    v.slug ||
    svc.slug ||
    ''
  ).toString().trim().toLowerCase();

  // 1. Keyword detection on directName and directSlug
  const matchedFromText = matchVehicleFamily(`${directName} ${directSlug}`);
  if (matchedFromText) {
    const finalName = (directName && directName.toLowerCase() !== 'vehicle') ? directName : matchedFromText.name;
    return { name: finalName, slug: matchedFromText.slug };
  }

  // 2. ID matching across candidate IDs
  const serviceIdCandidates = [
    item.service_id,
    item.serviceId,
    raw.service_id,
    item.unit_type_id,
    vInfo.vehicleTypeId,
    vInfo.vehicle_type_id,
    v.vehicleTypeId,
    v.vehicle_type_id,
    v.vehicleType?.id,
    item.vehicleTypeId
  ].filter(Boolean).map(x => String(x));

  for (const sId of serviceIdCandidates) {
    if (KNOWN_VEHICLE_TYPE_IDS[sId]) {
      return KNOWN_VEHICLE_TYPE_IDS[sId];
    }
  }

  // 3. Search in cachedServices / localStorage services
  let services = cachedServices || [];
  if (!services.length) {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i) || '';
        if (key.startsWith('pos_services_') || key.includes('service')) {
          const rawLocal = localStorage.getItem(key);
          if (rawLocal) {
            const parsed = JSON.parse(rawLocal);
            const list = Array.isArray(parsed) ? parsed : (parsed.data || parsed.services || []);
            if (Array.isArray(list) && list.length > 0) {
              services = list;
              break;
            }
          }
        }
      }
    } catch {}
  }

  for (const sId of serviceIdCandidates) {
    const matched = services.find((s: any) => {
      const cId = String(s.id || '');
      const ceId = String(s.existing_service_id || s.exisitng_service_id || '');
      const cuId = String(s.unit_type_id || '');
      const cvId = String(s.vehicle_type_id || s.vehicleTypeId || s.vehicle_type?.id || '');
      return (cId === sId || ceId === sId || cuId === sId || cvId === sId);
    });
    if (matched) {
      const sName = matched.name || matched.service_name || matched.vehicle_type?.name || 'Car';
      const sSlug = (matched.slug || matched.vehicle_type?.slug || sName).toLowerCase().replace(/\s+/g, '-');
      const fam = matchVehicleFamily(`${sName} ${sSlug}`);
      return {
        name: sName,
        slug: fam ? fam.slug : sSlug
      };
    }
  }

  if (directName && directName.toLowerCase() !== 'vehicle') {
    const slug = (directSlug || directName).toLowerCase().replace(/\s+/g, '-');
    return { name: directName, slug };
  }

  return { name: 'Car', slug: 'car' };
}

function handleRead(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  sync: SyncService,
  cache: OfflineCacheService,
  key: string
): Observable<HttpEvent<unknown>> {
  const urlWithParams = request.urlWithParams;
  const urlBase = request.url.split('?')[0];

  const buildOfflineResponse = async (): Promise<any> => {
    let body = await cache.get(key, urlWithParams, urlBase);

    // Merge pending offline activities from SQLite sync_queue into activity lists
    if (request.url.includes('/vehicle-activity')) {
      body = await mergePendingVehicleActivities(cache, body, request.url);
    } else if (request.url.includes('/facility-activity')) {
      body = await mergePendingFacilityActivities(cache, body, request.url);
    } else if (request.url.includes('/pass/list') || request.url.endsWith('/pass')) {
      body = await mergePassesFallback(cache, body, request.url);
    } else if (request.url.includes('/services')) {
      body = await mergeServicesFallback(cache, body, request.url);
    }

    return body;
  };

  const cachedResponse = (body: unknown) => of(new HttpResponse({ status: 200, url: request.url, body }));

  const emptyOperational = () =>
    of(
      new HttpResponse({
        status: 200,
        url: request.url,
        body: {
          success: true,
          offline: true,
          data: {
            activities: [],
            rows: [],
            passes: [],
            services: [],
            total: 0,
            meta: {
              pagination: { page: 1, limit: 25, total: 0, totalPages: 1 }
            }
          }
        }
      })
    );

  const fromCache = (fallback: () => Observable<HttpEvent<unknown>>) =>
    from(buildOfflineResponse()).pipe(
      switchMap((body) => {
        if (body !== null && body !== undefined) return cachedResponse(body);
        if (!sync.isOnline && isOperationalEndpoint(request.url)) return emptyOperational();
        return fallback();
      })
    );

  // Keep rows that are saved locally but not yet accepted by the server.
  const withLocalRows = async (body: unknown): Promise<unknown> => {
    if (request.url.includes('/vehicle-activity')) return mergePendingVehicleActivities(cache, body, request.url);
    if (request.url.includes('/facility-activity')) return mergePendingFacilityActivities(cache, body, request.url);
    return body;
  };

  const fromNetwork = () =>
    next(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.status === 200) {
          sync.noteApiReachable();
          cache.put(key, event.body).catch(() => undefined);
        }
      }),
      switchMap((event) => {
        if (!(event instanceof HttpResponse) || event.status !== 200) return of(event);
        return from(withLocalRows(event.body)).pipe(
          switchMap((body) => of(event.clone({ body })))
        );
      })
    );

  if (!sync.isOnline) {
    return fromCache(() => next(request));
  }

  // Start the API immediately — do not wait on SQLite before the network hop.
  // If a cache row is ready first, paint it; the network response still wins afterwards.
  const network$ = fromNetwork().pipe(
    catchError((error) =>
      isNetworkFailure(error)
        ? fromCache(() => throwError(() => error))
        : throwError(() => error)
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  const cacheFirst$ = from(buildOfflineResponse()).pipe(
    filter((body): body is NonNullable<typeof body> => body !== null && body !== undefined),
    switchMap((body) => cachedResponse(body)),
    takeUntil(network$)
  );

  return merge(cacheFirst$, network$);
}

async function mergePendingVehicleActivities(
  cache: OfflineCacheService,
  body: any,
  url: string
): Promise<any> {
  const pending = await cache.getPendingSyncItems('vehicle_activity');

  const parkingMatch = url.match(/\/parking\/([a-zA-Z0-9-]+)/);
  const parkingId = parkingMatch ? parkingMatch[1] : '';
  let cachedServices: any[] = [];
  if (parkingId) {
    try {
      const fromDb = await cache.get<any[]>(`pos_services_vehicle_${parkingId}`) ||
                     await cache.get<any[]>(`pos_services_${parkingId}`);
      if (Array.isArray(fromDb) && fromDb.length > 0) {
        cachedServices = fromDb;
      } else {
        const fromLocal = localStorage.getItem(`pos_services_vehicle_${parkingId}`) ||
                          localStorage.getItem(`pos_services_${parkingId}`);
        if (fromLocal) {
          const parsed = JSON.parse(fromLocal);
          cachedServices = Array.isArray(parsed) ? parsed : (parsed.data || parsed.services || []);
        }
      }
    } catch (_) {}
  }

  // Fallback to pos_active_sessions if body is null and URL matches a parking
  if (body === null || body === undefined) {
    if (parkingId) {
      const posSessions = await cache.get<any[]>(`pos_active_sessions_${parkingId}`);
      if (Array.isArray(posSessions) && posSessions.length > 0) {
        body = {
          success: true,
          offline: true,
          data: {
            activities: posSessions,
            rows: posSessions,
            total: posSessions.length,
            meta: { pagination: { page: 1, limit: posSessions.length, total: posSessions.length, totalPages: 1 } }
          }
        };
      }
    }
  }

  if (body === null || body === undefined) {
    if (pending.length === 0) return null;
    body = {
      success: true,
      offline: true,
      message: 'Offline vehicle activities',
      data: {
        activities: [],
        rows: [],
        total: 0,
        meta: {
          pagination: { page: 1, limit: 100, total: 0, totalPages: 1 }
        }
      }
    };
  }

  let activitiesList: any[] = [];
  if (Array.isArray(body?.data?.activities)) {
    activitiesList = [...body.data.activities];
  } else if (Array.isArray(body?.data?.rows)) {
    activitiesList = [...body.data.rows];
  } else if (Array.isArray(body?.activities)) {
    activitiesList = [...body.activities];
  } else if (Array.isArray(body?.rows)) {
    activitiesList = [...body.rows];
  } else if (Array.isArray(body?.data)) {
    activitiesList = [...body.data];
  } else if (Array.isArray(body)) {
    activitiesList = [...body];
  }

  for (const item of pending) {
    const refId = item.offlineReferenceId || `offline_${item.syncId}`;
    const isExit = !!(item.outTime || item.out_time || item.checkout || item.exit_time || item.type === 'exit' || item.status === 'completed' || item.status === 'COMPLETED');
    const reg = (
      item.vehicleInfo?.regNumber ||
      item.regNumber ||
      item.reg_number ||
      item.registration_number ||
      item.vehicle?.regNumber ||
      item.vehicle?.registration_number ||
      ''
    ).toString().replace(/[^A-Z0-9]/gi, '').toUpperCase();

    if (isExit) {
      for (const act of activitiesList) {
        const actRef = act.offline_reference_id || act.id || act.activity_id;
        const actReg = (
          act.vehicle?.regNumber ||
          act.vehicle?.registration_number ||
          act.vehicleInfo?.regNumber ||
          act.registration_number ||
          act.regNumber ||
          act.number ||
          ''
        ).toString().replace(/[^A-Z0-9]/gi, '').toUpperCase();
        if ((refId && actRef === refId) || (reg && actReg === reg)) {
          act.status = 'completed';
          act.outTime = item.outTime || item.exit_time || new Date().toISOString();
        }
      }
    } else {
      const exists = activitiesList.some((act) => {
        const actRef = act.offline_reference_id || act.id || act.activity_id;
        return actRef === refId;
      });

      if (!exists) {
        const nowIso = item.inTime || item.createdAt || new Date().toISOString();
        const vType = resolveVehicleType(item, cachedServices);
        const op = (item.inBy && item.inBy.name && item.inBy.name !== 'Offline Operator') ? item.inBy : getCurrentOperator();
        const newAct = {
          id: refId,
          activity_id: refId,
          activityId: refId,
          offline_reference_id: refId,
          inTime: nowIso,
          status: 'active',
          entry_type: (item.entry_type || 'walkins').toLowerCase(),
          service_type: 'vehicle',
          service_id: item.service_id,
          service_name: vType.name,
          category_name: vType.name,
          vehicle_type_name: vType.name,
          vehicle_type_slug: vType.slug,
          rate_id: item.rate_id,
          vehicleInfo: {
            ...(item.vehicleInfo || {}),
            regNumber: reg,
            vehicleTypeId: item.vehicleInfo?.vehicleTypeId || item.service_id,
            vehicleTypeName: vType.name,
            vehicleTypeSlug: vType.slug
          },
          vehicle: {
            id: `veh_${refId}`,
            regNumber: reg,
            registration_number: reg,
            vehicleTypeId: item.vehicleInfo?.vehicleTypeId || item.service_id,
            vehicleType: {
              name: vType.name,
              slug: vType.slug
            }
          },
          total_amount: 0,
          transactions: [],
          inBy: {
            id: op.id,
            name: op.name,
            firstName: op.firstName,
            lastName: op.lastName,
            email: op.email
          },
          operator: op.name,
          isOfflineLocal: true,
          sync_status: 'pending',
          createdAt: nowIso
        };
        activitiesList.unshift(newAct);
      }
    }
  }

  // Ensure any cached or pending rows have accurate operator and resolved vehicle category
  const activeOperator = getCurrentOperator();
  for (const act of activitiesList) {
    if (!act.inBy || act.inBy?.name === 'Offline Operator' || act.operator === 'Offline Operator') {
      act.inBy = {
        id: activeOperator.id,
        name: activeOperator.name,
        firstName: activeOperator.firstName,
        lastName: activeOperator.lastName,
        email: activeOperator.email
      };
      act.operator = activeOperator.name;
    }

    const resolvedType = resolveVehicleType(act, cachedServices);
    const vReg = (
      act.vehicle?.regNumber ||
      act.vehicle?.registration_number ||
      act.vehicleInfo?.regNumber ||
      act.registration_number ||
      act.regNumber ||
      ''
    ).toString().trim().toUpperCase();

    if (!act.vehicle) {
      act.vehicle = {
        id: `veh_${act.id || act.activity_id}`,
        regNumber: vReg,
        registration_number: vReg,
        vehicleTypeId: act.service_id || act.vehicleInfo?.vehicleTypeId,
        vehicleType: { name: resolvedType.name, slug: resolvedType.slug }
      };
    } else {
      const currentName = act.vehicle.vehicleType?.name || '';
      const currentSlug = act.vehicle.vehicleType?.slug || '';
      if (
        !currentSlug ||
        !currentName ||
        currentName.toLowerCase() === 'vehicle' ||
        currentSlug.toLowerCase() === 'vehicle' ||
        (resolvedType.name !== 'Vehicle' && currentName !== resolvedType.name)
      ) {
        act.vehicle.vehicleType = {
          name: resolvedType.name,
          slug: resolvedType.slug
        };
      }
    }

    if (!act.category_name || act.category_name.toLowerCase() === 'vehicle') {
      act.category_name = resolvedType.name;
    }
    if (!act.service_name || act.service_name.toLowerCase() === 'vehicle') {
      act.service_name = resolvedType.name;
    }
    if (!act.vehicle_type_name || act.vehicle_type_name.toLowerCase() === 'vehicle') {
      act.vehicle_type_name = resolvedType.name;
    }
    act.vehicle_type_slug = resolvedType.slug;
  }

  // If active sessions are requested, filter out completed/exited sessions
  if (url && url.toLowerCase().includes('status=active')) {
    activitiesList = activitiesList.filter(
      (act) => (act.status === 'active' || act.status === 'ACTIVE') && !act.outTime && !act.out_time
    );
  }

  if (body && typeof body === 'object') {
    if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
      body.data.activities = activitiesList;
      body.data.rows = activitiesList;
      body.data.total = activitiesList.length;
      body.data.meta = {
        ...(body.data.meta || {}),
        pagination: {
          page: 1,
          limit: Math.max(activitiesList.length, 25),
          total: activitiesList.length,
          totalPages: 1
        }
      };
    } else {
      body.data = {
        activities: activitiesList,
        rows: activitiesList,
        total: activitiesList.length,
        meta: {
          pagination: {
            page: 1,
            limit: Math.max(activitiesList.length, 25),
            total: activitiesList.length,
            totalPages: 1
          }
        }
      };
    }
    body.activities = activitiesList;
    body.rows = activitiesList;
    body.total = activitiesList.length;
  }

  return body;
}

async function mergePendingFacilityActivities(
  cache: OfflineCacheService,
  body: any,
  _url: string
): Promise<any> {
  const pending = await cache.getPendingSyncItems('facility_activity');

  if (body === null || body === undefined) {
    if (pending.length === 0) return null;
    body = {
      success: true,
      offline: true,
      message: 'Offline facility activities',
      data: {
        activities: [],
        rows: [],
        total: 0,
        meta: {
          pagination: { page: 1, limit: 100, total: 0, totalPages: 1 }
        }
      }
    };
  }

  let activitiesList: any[] = [];
  if (Array.isArray(body?.data?.activities)) {
    activitiesList = [...body.data.activities];
  } else if (Array.isArray(body?.data?.rows)) {
    activitiesList = [...body.data.rows];
  } else if (Array.isArray(body?.activities)) {
    activitiesList = [...body.activities];
  } else if (Array.isArray(body?.rows)) {
    activitiesList = [...body.rows];
  } else if (Array.isArray(body?.data)) {
    activitiesList = [...body.data];
  } else if (Array.isArray(body)) {
    activitiesList = [...body];
  }

  for (const item of pending) {
    const refId = item.offlineReferenceId || `offline_${item.syncId}`;
    const exists = activitiesList.some((act) => {
      const actRef = act.offline_reference_id || act.id || act.activity_id;
      return actRef === refId;
    });

    if (!exists) {
      const nowIso = item.inTime || item.createdAt || new Date().toISOString();
      const qty = item.quantity || (Array.isArray(item.items) ? item.items.reduce((s: number, i: any) => s + (Number(i.quantity) || 0), 0) : 1);
      const op = (item.inBy && item.inBy.name && item.inBy.name !== 'Offline Operator') ? item.inBy : getCurrentOperator();
      const vReg = (item.vehicleInfo?.regNumber || item.vehicle?.regNumber || item.regNumber || item.registration_number || '').toString().trim().toUpperCase();
      const vType = resolveVehicleType(item);

      const newAct = {
        id: refId,
        activity_id: refId,
        rowId: refId,
        offline_reference_id: refId,
        inTime: nowIso,
        status: 'active',
        service_id: item.service_id,
        rate_id: item.rate_id,
        quantity: qty,
        qty: qty,
        items: item.items || [],
        customer: item.customer || item.customerInfo,
        vehicleInfo: item.vehicleInfo || (vReg ? {
          regNumber: vReg,
          vehicleTypeId: item.service_id,
          vehicleTypeName: vType.name,
          vehicleTypeSlug: vType.slug
        } : undefined),
        vehicle: item.vehicle || (vReg ? {
          id: `veh_${refId}`,
          regNumber: vReg,
          registration_number: vReg,
          vehicleTypeId: item.service_id,
          vehicleType: { name: vType.name, slug: vType.slug }
        } : undefined),
        notes: item.notes || item.remarks || '',
        total_amount: item.total_amount || 0,
        total: item.total_amount || 0,
        transactions: [],
        inBy: {
          id: op.id,
          name: op.name,
          firstName: op.firstName,
          lastName: op.lastName,
          email: op.email
        },
        operator: op.name,
        isOfflineLocal: true,
        sync_status: 'pending',
        createdAt: nowIso
      };
      activitiesList.unshift(newAct);
    }
  }

  // Ensure any cached or pending rows with placeholder 'Offline Operator' display the actual current operator
  const activeOperator = getCurrentOperator();
  for (const act of activitiesList) {
    if (!act.inBy || act.inBy?.name === 'Offline Operator' || act.operator === 'Offline Operator') {
      act.inBy = {
        id: activeOperator.id,
        name: activeOperator.name,
        firstName: activeOperator.firstName,
        lastName: activeOperator.lastName,
        email: activeOperator.email
      };
      act.operator = activeOperator.name;
    }
    if (!act.vehicle && (act.vehicleInfo?.regNumber || act.registration_number)) {
      const vReg = act.vehicleInfo?.regNumber || act.registration_number;
      const vType = resolveVehicleType(act);
      act.vehicle = {
        id: `veh_${act.id || act.activity_id}`,
        regNumber: vReg,
        registration_number: vReg,
        vehicleType: { name: vType.name, slug: vType.slug }
      };
    }
  }

  if (body && typeof body === 'object') {
    if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
      body.data.activities = activitiesList;
      body.data.rows = activitiesList;
      body.data.total = activitiesList.length;
      body.data.meta = {
        ...(body.data.meta || {}),
        pagination: {
          page: 1,
          limit: Math.max(activitiesList.length, 25),
          total: activitiesList.length,
          totalPages: 1
        }
      };
    } else {
      body.data = {
        activities: activitiesList,
        rows: activitiesList,
        total: activitiesList.length,
        meta: {
          pagination: {
            page: 1,
            limit: Math.max(activitiesList.length, 25),
            total: activitiesList.length,
            totalPages: 1
          }
        }
      };
    }
    body.activities = activitiesList;
    body.rows = activitiesList;
    body.total = activitiesList.length;
  }

  return body;
}

async function mergePassesFallback(
  cache: OfflineCacheService,
  body: any,
  url: string
): Promise<any> {
  if (body !== null && body !== undefined) {
    return body;
  }

  const parkingMatch = url.match(/[?&]parking_id=([a-zA-Z0-9-]+)/) || url.match(/\/parking\/([a-zA-Z0-9-]+)/);
  const parkingId = parkingMatch ? parkingMatch[1] : '';

  if (parkingId) {
    const cachedPasses = await cache.get<any[]>(`pos_active_passes_${parkingId}`);
    if (Array.isArray(cachedPasses) && cachedPasses.length > 0) {
      return {
        success: true,
        offline: true,
        message: 'Offline cached passes',
        data: {
          passes: cachedPasses,
          activities: cachedPasses,
          rows: cachedPasses,
          total: cachedPasses.length,
          meta: {
            pagination: { page: 1, limit: cachedPasses.length, total: cachedPasses.length, totalPages: 1 }
          }
        }
      };
    }
  }

  return null;
}

function requestedServiceKind(url: string): 'pass' | 'facility' | 'vehicle' | '' {
  try {
    const value = (new URLSearchParams(url.split('?')[1] || '').get('service_type') || '').toLowerCase();
    if (value.startsWith('pass')) return 'pass';
    if (value.startsWith('facilit')) return 'facility';
    if (value.startsWith('vehicle')) return 'vehicle';
  } catch (_) {}
  return '';
}

function servicesFromBody(body: any): any[] {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data?.services)) return body.data.services;
  if (Array.isArray(body?.services)) return body.services;
  if (Array.isArray(body?.data)) return body.data;
  return [];
}

function wrapServices(list: any[]): any {
  return {
    success: true,
    offline: true,
    message: 'Offline cached services',
    data: { services: list, data: list },
    services: list
  };
}

async function readServiceCatalog(
  cache: OfflineCacheService,
  parkingId: string,
  kind: 'pass' | 'facility' | 'vehicle'
): Promise<any[] | null> {
  const keys = kind === 'vehicle'
    ? [`pos_services_vehicle_${parkingId}`, `pos_services_${parkingId}`]
    : [`pos_services_${kind}_${parkingId}`];

  for (const key of keys) {
    const fromDb = await cache.get<any[]>(key);
    if (Array.isArray(fromDb) && fromDb.length > 0) return fromDb;
    try {
      const raw = localStorage.getItem(key) || localStorage.getItem(`cache_${key}`);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const list = Array.isArray(parsed) ? parsed : servicesFromBody(parsed);
      if (list.length > 0) return list;
    } catch (_) {}
  }

  const saved = await cache.findByFragment(`/parking/${parkingId}/services`);
  const match = saved.find((row) => row.key.toLowerCase().includes(`service_type=${kind}`));
  const fromHttp = match ? servicesFromBody(match.body) : [];
  return fromHttp.length > 0 ? fromHttp : null;
}

async function mergeServicesFallback(
  cache: OfflineCacheService,
  body: any,
  url: string
): Promise<any> {
  const parkingMatch = url.match(/\/parking\/([a-zA-Z0-9-]+)/);
  const parkingId = parkingMatch ? parkingMatch[1] : '';
  const kind = requestedServiceKind(url);

  if (parkingId && kind) {
    const catalog = await readServiceCatalog(cache, parkingId, kind);
    if (catalog && catalog.length > 0) return wrapServices(catalog);
  }

  const list = servicesFromBody(body);
  if (list.length > 0 && kind !== 'pass' && kind !== 'facility') {
    return body;
  }

  if (parkingId && kind !== 'pass' && kind !== 'facility') {
    const vehicleCatalog = await readServiceCatalog(cache, parkingId, 'vehicle');
    if (vehicleCatalog && vehicleCatalog.length > 0) return wrapServices(vehicleCatalog);
  }

  return body;
}

function isOperationalEndpoint(url: string): boolean {
  return (
    url.includes('/vehicle-activity') ||
    url.includes('/facility-activity') ||
    url.includes('/facility/parking/') ||
    url.includes('/facilities') ||
    url.includes('/pass') ||
    (url.includes('/parking/') && url.includes('/services'))
  );
}

function cacheKey(request: HttpRequest<unknown>, api: ApiService): string {
  const scope = [
    api.getCurrentUser()?.id ?? '',
    request.headers.get('x-org-id') ?? api.getCurrentOrganizationId() ?? '',
    request.headers.get('x-parking-id') ?? api.getCurrentParkingId() ?? '',
    request.headers.get('x-user-pov') ?? api.getUserPov() ?? ''
  ].join('|');
  return `${scope}|${request.urlWithParams}`;
}

function isNetworkFailure(error: unknown): boolean {
  return error instanceof HttpErrorResponse && (error.status === 0 || error.status === 504);
}
